/**
 * Where a record list opens a record (`table.row_open`, I-3641). A list only
 * knows "open this row", with `router.push` in its handler, so the opener lets it
 * run and takes over the navigation it starts. A handler that opens a dialog
 * starts none and simply opens it here.
 */
import type { Router } from "vue-router";
import { connectTileId, reportConnectTile } from "@blpde/connect";
import type { NxRowOpener } from "@blpde/nx-ui";
import { tileSplitReuses, useTilesEnabled } from "./tileNav";
import { browserTabUrl } from "./tileWindows";
import { useTileWorkspaces } from "./useTileWorkspaces";

/** A handler that loads something first still navigates within this time. */
const CAPTURE_MS = 1000;

export function createRecordOpener(router: Router): NxRowOpener {
  type Place = Parameters<NxRowOpener>[0];
  let pending: Place | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const tilesEnabled = useTilesEnabled();

  function openAt(place: Place, path: string, from: string): void {
    if (place === "tab") {
      window.open(browserTabUrl(path), "_blank", "noopener");
      return;
    }
    if (connectTileId()) {
      reportConnectTile({
        open:
          typeof place === "object"
            ? { path, target: "tile", tileId: place.tileId }
            : { path, target: place },
      });
      return;
    }
    const tiles = useTileWorkspaces();
    const active = tiles.active.value;
    if (typeof place === "object") tiles.openInTile(place.tileId, path);
    else if (place === "workspace") tiles.addWorkspace("", path);
    else if (from === "/tiles" && active) {
      if (place === "split")
        tiles.openFromTile(active.activeTileId, path, tileSplitReuses());
      else tiles.splitTile(active.id, active.activeTileId, "right", path);
    } else if (place === "split" && active && tileSplitReuses()) {
      tiles.openInTile(active.activeTileId, path);
    } else tiles.openPageAndBeside(from === "/tiles" ? null : from, path);
    void router.push("/tiles");
  }

  router.beforeEach((to, from) => {
    if (!pending) return true;
    const place = pending;
    pending = null;
    openAt(place, to.fullPath, from.fullPath);
    return false;
  });

  return (place, open) => {
    // Without tabs and tiles, every place but a browser tab is this page.
    if (place !== "tab" && !tilesEnabled.value) {
      open();
      return;
    }
    if (timer) clearTimeout(timer);
    pending = place;
    timer = setTimeout(() => {
      pending = null;
    }, CAPTURE_MS);
    open();
  };
}
